import { useCallback, useState } from 'react';

/**
 * OS-level notifications — the durable half of "notify with sound".
 *
 * THE DISTINCTION THAT MATTERS: autoplay is not a requestable permission and
 * notifications are. `Notification.requestPermission()` is granted once PER
 * ORIGIN and survives refreshes, new tabs and browser restarts, where the
 * audio unlock in `use-alert-sound` dies with every document. Audio is a
 * per-session convenience; this is the part that can be relied on.
 *
 * It also reaches the case audio cannot: a tab that is open but not looked
 * at — the CRM on a second monitor while the operator works elsewhere. The
 * chime plays there, but nothing is drawn where their eyes are.
 *
 * NO SERVICE WORKER. This is deliberate and is the whole reason it is half a
 * day rather than four. A service worker is needed only for the
 * browser-CLOSED case; conflating the two is what makes "add a notification
 * sound" sound like a week.
 */

export type NotifyPermission = 'unsupported' | 'default' | 'granted' | 'denied';

const current = (): NotifyPermission => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as NotifyPermission;
};

export interface DesktopNotifications {
  permission: NotifyPermission;
  /** True when there is a point in offering the button. */
  canAsk: boolean;
  /** MUST be called from a user gesture — see the note on `ask`. */
  ask: () => Promise<NotifyPermission>;
  /** Draws one, or does nothing. Never throws. */
  show: (title: string, body?: string, tag?: string) => void;
}

export function useDesktopNotifications(): DesktopNotifications {
  const [permission, setPermission] = useState<NotifyPermission>(current);

  /**
   * Ask, from a click.
   *
   * ONE SHOT, and that is why this is never called on mount. A prompt fired
   * on page load and dismissed is close to unrecoverable: Chrome will not
   * ask again, and the operator has to find it in site settings. Spend the
   * one chance on somebody who has just pressed a button that says what it
   * is for.
   */
  const ask = useCallback(async (): Promise<NotifyPermission> => {
    if (!('Notification' in window)) return 'unsupported';
    try {
      const result = (await Notification.requestPermission()) as NotifyPermission;
      setPermission(result);
      return result;
    } catch {
      // Safari used to throw on the promise form. Read the state back.
      const now = current();
      setPermission(now);
      return now;
    }
  }, []);

  const show = useCallback(
    (title: string, body?: string, tag?: string) => {
      if (permission !== 'granted') return;
      /*
       * NOT when the tab is already in front. The operator is looking at the
       * app; the toast and the bell have it, and an OS banner over the thing
       * it is telling you about is pure noise.
       */
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;

      try {
        // `tag` collapses repeats: a reminder and its chase for the same
        // follow-up replace one another rather than stacking two banners.
        new Notification(title, { body, tag, icon: '/logo.png' });
      } catch {
        // Some platforms require a service worker for `new Notification`
        // (Android Chrome). Nothing to do; the in-app path still fired.
      }
    },
    [permission],
  );

  return {
    permission,
    canAsk: permission === 'default',
    ask,
    show,
  };
}
